/**
 * @file AddUserModal.tsx
 * @description
 * -----------------------------------------------------
 * Componente modal de invitar usuario
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2026-03-30
 * @author DLTCode
 * @author Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 * */
"use client";

import React, { useState, useTransition } from "react";
import InputComponent from "@/components/ui/InputComponent";
import { useTranslations } from "next-intl";
import SelectComponent from "@/components/ui/SelectComponent";
import ModalComponent from "@/components/modals/ModalComponent";
import { toast } from "sonner";
import { USER_ROLES } from "@/constants/roles";
import { fetchInviteUser, UserRoles } from "@/features/users";
import { InviteBody } from "@/types/user/user";
interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose
}) => {
  const [formData, setFormData] = useState<InviteBody>({
    mail: "",
    role: ""
  });
  const [errors, setErrors] = useState<{
    [key: string]: string[];
  }>({});
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("Modals");
  const labels = useTranslations("Labels");
  const placeholders = useTranslations("Placeholders");
  const options = useTranslations("SelectComponent.Options");
  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors(prevErrors => {
        const newErrors = {
          ...prevErrors
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handleSubmit = async (formData_0: FormData) => {
    startTransition(async () => {
      try {
        const body = {
          mail: formData_0.get("mail") as string,
          role: formData_0.get("role") as UserRoles
        };
        const response = await fetchInviteUser(body);
        if (response && "error" in response) {
          toast.error(response.message);
          return;
        }
        toast.success(response.message);
        onClose();
        setFormData({
          mail: "",
          role: ""
        });
      } catch (error) {
        if (error instanceof Error) {
          setErrors({
            mail: [error.message]
          });
        }
      }
    });
  };
  return <ModalComponent title={t("addUserToCompany.title")} isOpen={isOpen} onClose={onClose} formAction={handleSubmit} formData={{
    mail: formData.mail,
    role: formData.role
  }} loadingConfirm={isPending} buttonSubmitText="buttonAccept" buttonCancel={true} hideSubmitIcon={true} disabledConfirm={!formData.mail || !formData.role}>
      <InputComponent id="addMail" name="mail" label={labels("labelMail")} type="text" placeholder={placeholders("placeholderMail")} value={formData.mail} onChange={handleChange} error={errors.mail && errors.mail[0]} className="input--full" isRequired={true} />
      <SelectComponent id="addRole" label={labels("labelRole")} name={"role"} options={USER_ROLES.map(role => ({
      value: role.value,
      label: options(role.label)
    }))} value={formData.role} onChange={handleChange} placeholder={placeholders("placeholderRole")} error={errors.role && errors.role[0]} className="select--full" isRequired={true} />
    </ModalComponent>;
};
export default AddUserModal;